import React, { createElement, Fragment, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { autocomplete } from '@algolia/autocomplete-js';
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';
// 1. RE-ADD the classic theme for panel functionality
import '@algolia/autocomplete-theme-classic';
import start_data from '../asset/start.json';
import { serverUrl } from '../util/constants';

function book2pdf(book_page, vol_name) {
  let ret_page = book_page + (start_data[vol_name] || 0);
  switch (vol_name) {
    case 'EN/Volume_10':
      if (book_page > 398) return ret_page + 4;
      break;
    case 'EN/Volume_15':
      if (book_page > 704) return ret_page + 2;
      break;
    case 'EN/Volume_17_02':
      if (book_page > 300) return ret_page + 2;
      break;
    default:
      return ret_page;
  }
  return ret_page;
}

let fuse = null;
let fusePromise = null;

async function getFuse() {
  if (fuse) return fuse;
  if (fusePromise) return await fusePromise;

  fusePromise = Promise.all([
    import('fuse.js'),
    import('../asset/suggestion.json'),
    import('../asset/suggestion-mr.json'),
    import('../asset/suggestion-hi.json'),
  ])
    .then(([FuseModule, en, mr, hi]) => {
      const Fuse = FuseModule.default;
      const suggestions = [
        ...(en.default || en),
        ...(mr.default || mr),
        ...(hi.default || hi),
      ];

      const updatedSuggestions = suggestions.map((item) => ({
        ...item,
        label: `"${item.label.replace(/^[\d+.\s\-()]+/, '')}"`,
        page_start: book2pdf(item.page_start, item.vol_name),
      }));

      fuse = new Fuse(updatedSuggestions, {
        findAllMatches: true,
        minMatchCharLength: 3,
        threshold: 0.3,
        ignoreLocation: true,
        useExtendedSearch: true,
        keys: ['label'],
      });
      return fuse;
    })
    .catch((err) => {
      fusePromise = null;
      throw err;
    });

  return await fusePromise;
}

const getUrl = (item) =>
  `${serverUrl}books/baws/${item.vol_name}/pdf/${item.page_start}`;

const AutoComplete = (props) => {
  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const panelRootRef = useRef(null);
  const rootRef = useRef(null);

  const propsRef = useRef(props);
  useEffect(() => {
    propsRef.current = props;
  });

  useEffect(() => {
    if (searchRef.current && props.defaultValue !== undefined) {
      searchRef.current.setQuery(props.defaultValue);
    }
  }, [props.defaultValue]);

  useEffect(() => {
    if (!containerRef.current || searchRef.current) {
      return;
    }

    const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
      key: 'RECENT_SEARCH',
      limit: 6,
      transformSource({ source }) {
        return {
          ...source,
          onSelect(params) {
            if (params.item.label) {
              params.setIsOpen(false);
              params.setQuery(params.item.label);
              propsRef.current.onSelect(params.item.label);
            }
          },
        };
      },
    });

    const initializeSearch = () => {
      try {
        searchRef.current = autocomplete({
          container: containerRef.current,

          // 2. OVERRIDE only the input with Tailwind classes
          classNames: {
            input:
              'w-full p-2 text-base border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none',
          },

          renderer: { createElement, Fragment, render: () => {} },
          render({ children }, root) {
            if (!panelRootRef.current || rootRef.current !== root) {
              rootRef.current = root;
              panelRootRef.current?.unmount();
              panelRootRef.current = createRoot(root);
            }
            panelRootRef.current.render(children);
          },
          plugins: [recentSearchesPlugin],
          getSources() {
            return [
              {
                sourceId: 'label',
                async getItems({ query }) {
                  if (!query || query.length < 3) return [];

                  const fuseInstance = await getFuse();
                  const terms = query.trim().split(/\s+/).filter(Boolean);

                  // If multiple words are typed, require ALL of them to match anywhere in the string
                  if (terms.length > 1) {
                    const extendedQuery = {
                      $and: terms.map((term) => ({ label: `'${term}` })),
                    };
                    return fuseInstance.search(extendedQuery);
                  }

                  return fuseInstance.search(query);
                },
                getItemInputValue({ item }) {
                  return item.item.label;
                },
                onSelect(params) {
                  if (params.item.item.label) {
                    params.setIsOpen(false);
                    propsRef.current.onSelect(params.item.item.label);
                    params.setQuery(params.item.item.label);
                  }
                },
                templates: {
                  item({ item }) {
                    return (
                      <>
                        <h6 style={{ display: 'flex', gap: '0.5em' }}>
                          {item.item.label}
                          <a
                            href={getUrl(item.item)}
                            onClick={(e) => {
                              e.preventDefault(); // preventDefault added here
                              e.stopPropagation();
                              propsRef.current.onSetBookUrl(
                                e,
                                item.item.vol_name,
                                item.item.page_start
                              );
                            }}
                          >
                            Open Direct
                          </a>
                        </h6>
                        <p style={{ fontSize: '0.75em' }}>
                          {item.item.vol_name}
                        </p>
                      </>
                    );
                  },
                },
              },
            ];
          },
          onSubmit(params) {
            if (params.state.query) {
              propsRef.current.onSelect(params.state.query);
            }
          },
          placeholder:
            window.innerWidth < 640
              ? 'Search BAWS'
              : 'Search the writings, speeches and letters',
          detachedMediaQuery: 'none',
          initialState: {
            query: props.defaultValue,
          },
        });
      } catch (err) {
        console.error('Failed to initialize search:', err);
      }
    };

    initializeSearch();

    return () => {
      if (searchRef.current) {
        searchRef.current.destroy();
        searchRef.current = null;
      }
      panelRootRef.current?.unmount();
    };
  }, []);

  return <div ref={containerRef} className="relative z-[9999]" />;
};

export default AutoComplete;
